import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyEmail, resendVerificationEmail } from '../../services/api';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  green: '#10B981',
  greenDark: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  greenText: '#065F46',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Brand mark ───────────────────────────────────────────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 2px 8px ${C.accent}55`,
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT, fontSize: 18, fontWeight: 700,
        color: C.text, letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Main verification ────────────────────────────────────────────────────────

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // 'loading' | 'success' | 'error'
  const [state, setState] = useState(token ? 'loading' : 'error');
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : 'No verification token in this link. Please check the link in your email.'
  );

  // Resend flow state — only used when state === 'error'.
  const [resendEmail, setResendEmail] = useState('');
  // 'idle' | 'sending' | 'sent' | 'error'
  const [resendStatus, setResendStatus] = useState('idle');
  const [resendError, setResendError] = useState('');

  async function handleResend(e) {
    e.preventDefault();
    if (!resendEmail.trim()) {
      setResendError('Please enter your email address.');
      setResendStatus('error');
      return;
    }
    setResendStatus('sending');
    setResendError('');
    try {
      await resendVerificationEmail(resendEmail.trim());
      // The server returns the same generic message regardless of whether
      // the address is registered — we mirror that here. Don't reveal whether
      // the email exists.
      setResendStatus('sent');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setResendError('Too many resend attempts. Please wait a few minutes and try again.');
      } else if (status === 400) {
        setResendError(err?.response?.data?.error || 'Please check your email address.');
      } else {
        setResendError('Something went wrong. Please try again in a moment.');
      }
      setResendStatus('error');
    }
  }

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    verifyEmail(token)
      .then(() => {
        if (cancelled) return;
        setState('success');
      })
      .catch((err) => {
        if (cancelled) return;
        const serverMessage = err?.response?.data?.error;
        setErrorMessage(serverMessage || 'We couldn’t verify this link. It may have expired or already been used.');
        setState('error');
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div style={{
      minHeight: '100vh',
      background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, ${C.accent}1c 0%, ${C.accent}00 60%)`,
      fontFamily: BODY_FONT,
      color: C.text,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 20px 60px',
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ marginBottom: 24 }}>
          <LanternBrand />
        </div>

        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: '36px 28px',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
          textAlign: 'center',
        }}>
          {state === 'loading' && (
            <>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                border: `3px solid ${C.border}`,
                borderTopColor: C.accent,
                margin: '0 auto 18px',
                animation: 'ev-spin 800ms linear infinite',
              }} />
              <h1 style={{
                margin: '0 0 6px',
                fontFamily: HEADING_FONT,
                fontSize: 22,
                fontWeight: 700,
                color: C.text,
                letterSpacing: '-0.02em',
              }}>Verifying your email&hellip;</h1>
              <p style={{ margin: 0, fontSize: 14, color: C.textMuted }}>
                Just a moment while we confirm the link.
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: '#fff',
                border: `2px solid ${C.greenBorder}`,
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.18)',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12.5l4.5 4.5L19 7.5" stroke={C.greenDark} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1 style={{
                margin: '0 0 6px',
                fontFamily: HEADING_FONT,
                fontSize: 24,
                fontWeight: 700,
                color: C.text,
                letterSpacing: '-0.02em',
              }}>Email verified</h1>
              <p style={{
                margin: '0 auto 22px',
                fontSize: 14,
                color: C.textMuted,
                lineHeight: 1.55,
                maxWidth: 360,
              }}>
                Your account is ready. Sign in to set up your child&rsquo;s profile and start using Lantern.
              </p>
              <Link
                to="/parent/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 22px',
                  minHeight: 44,
                  background: C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontFamily: BODY_FONT,
                  fontSize: 15,
                  fontWeight: 600,
                  textDecoration: 'none',
                  boxShadow: '0 1px 0 rgba(0,61,153,0.4) inset, 0 1px 2px rgba(0,102,255,0.25), 0 4px 12px rgba(0,102,255,0.18)',
                }}
              >
                Sign in
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </>
          )}

          {state === 'error' && (
            <>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: '#fff',
                border: `2px solid ${C.redBorder}`,
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(239, 68, 68, 0.15)',
              }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9.5" stroke={C.redText} strokeWidth="2"/>
                  <path d="M12 7v6M12 16v.5" stroke={C.redText} strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h1 style={{
                margin: '0 0 6px',
                fontFamily: HEADING_FONT,
                fontSize: 22,
                fontWeight: 700,
                color: C.text,
                letterSpacing: '-0.02em',
              }}>Link couldn&rsquo;t be verified</h1>
              <p style={{
                margin: '0 auto 22px',
                fontSize: 14,
                color: C.textMuted,
                lineHeight: 1.55,
                maxWidth: 360,
              }}>
                {errorMessage}
              </p>
              {resendStatus === 'sent' ? (
                <div style={{
                  marginTop: 4,
                  padding: '12px 14px',
                  background: C.greenSoft,
                  border: `1px solid ${C.greenBorder}`,
                  borderRadius: 10,
                  color: C.greenText,
                  fontSize: 13,
                  textAlign: 'left',
                }}>
                  If an account exists for that address and still needs verification,
                  a new email is on its way. Please check your inbox (and spam folder).
                </div>
              ) : (
                <form onSubmit={handleResend} noValidate style={{ textAlign: 'left' }}>
                  <p style={{
                    margin: '0 0 10px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                  }}>
                    Need a new verification email?
                  </p>
                  <input
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    value={resendEmail}
                    onChange={(e) => { setResendError(''); setResendStatus('idle'); setResendEmail(e.target.value); }}
                    placeholder="you@example.com"
                    disabled={resendStatus === 'sending'}
                    aria-label="Email address"
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: '10px 12px',
                      fontFamily: BODY_FONT,
                      fontSize: 14,
                      color: C.text,
                      outline: 'none',
                      background: '#fff',
                      width: '100%',
                      marginBottom: 10,
                    }}
                  />
                  {resendError && (
                    <div role="alert" style={{
                      marginBottom: 10,
                      padding: '8px 12px',
                      background: C.redSoft,
                      border: `1px solid ${C.redBorder}`,
                      borderRadius: 8,
                      color: C.redText,
                      fontSize: 12.5,
                      fontWeight: 500,
                    }}>
                      {resendError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={resendStatus === 'sending'}
                    style={{
                      width: '100%',
                      minHeight: 44,
                      padding: '12px 22px',
                      background: C.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontFamily: BODY_FONT,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: resendStatus === 'sending' ? 'not-allowed' : 'pointer',
                      opacity: resendStatus === 'sending' ? 0.7 : 1,
                    }}
                  >
                    {resendStatus === 'sending' ? 'Sending…' : 'Resend verification email'}
                  </button>
                </form>
              )}

              <p style={{
                margin: '18px 0 0',
                fontSize: 13,
                color: C.textSubtle,
              }}>
                Already verified?{' '}
                <Link to="/parent/login" style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}>
                  Sign in
                </Link>
                {'  ·  '}
                <Link to="/parent/register" style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}>
                  Sign up again
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes ev-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
